import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { NotificationManager } from "react-notifications";
import { showLoader, hideLoader } from "../../store/common/slice";
import BllinkUtils from "../utils/utils";
import { constants } from "../constants/constants";
import {getReportRows} from "./ReportData";

/**
 * @typedef {object} PdfSettings
 * @property {number} chunkSize
 * @property {'p' | 'l'} [orientation='p']
 **/

/**
    @typedef {Object} PdfIframeProps
    @property {ReportData} reportData
    @property {string} reportTitle
    @param {string} partnerID
    @param {string} name
    @property {boolean} disabled
    @property {boolean} disablePdfSave
    @property {function} onPostingDataDone
    @property {(string) => void} onPdfGenerated
    @property {string} buildingAddress
    @property {string} titleRow
    @property {PdfSettings} pdfSettings
*/
/**
    @param {PdfIframeProps} props
    @returns {JSX.Element}
*/

export function PdfIframe({
  reportData,
  reportTitle,
  disabled,
  disablePdfSave,
  onPostingDataDone,
  onPdfGenerated,
  partnerID,
  partnerName,
  buildingAddress,
  titleRow,
  pdfSettings,
}) {
    const dispatch = useDispatch();
    const application = useRef();
    const linkRef = useRef();

    const buildingTitleText = useSelector(state => state.buildings.titleText);
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);

    const [pdfDataUrl, setPdfDataUrl] = useState(null);
    const [fileName, setFileName] = useState('');

    /**
     * catch errors from iframe
     * @param {MessageEvent} event
     * @returns
     */

    const handleIframeMessage = useCallback((event) => {
        if (event.data.bllinkPdfRender === true) {
            if (event.data.iframeError) {
                NotificationManager.error(event.data.error.message);
                dispatch(hideLoader(false));
                return;
            }
            // saving pdf
            setPdfDataUrl(event.data.pdfDataUrl);
            if (!disablePdfSave) {
                linkRef.current.click();
            }
            if (onPdfGenerated) {
                onPdfGenerated(event.data.pdfDataBlob)
            }
            dispatch(hideLoader(false));
        }
    }, [disablePdfSave, onPdfGenerated])

    useEffect(() => {
        const app = application.current.contentWindow;
        app.addEventListener('message', handleIframeMessage);
        return () => app.removeEventListener('message', handleIframeMessage);
    }, [handleIframeMessage]);

    useEffect(async () => {
        if (!disabled) {
            const currentDate = BllinkUtils.BllinkTime.parseDate(Date.now()).split('/').join('_');
            const fileName = `${currentDate}-${reportTitle} - ${buildingTitleText}.pdf`;
            setFileName(fileName);
            dispatch(showLoader());
            const data = await getReportRows(reportData, 'pdf');
            if (data == null) {
                onPostingDataDone(false);
                dispatch(hideLoader());
                return;
            }
            // passing data to iframe - add new keys if need to extract pdf structure
            application.current.contentWindow.postMessage({
                applicationData: true,
                data,
                reportTitle,
                buildingAddress,
                partnerName,
                titleRow,
                currentBuildingData,
                ...pdfSettings,
            });
            // allows to create pdf again
            onPostingDataDone();
        }
    }, [disabled]);

    /**
     * create iFrame url to display PdfTableReportRender component
     * adding partnerID if exist
     * @return string
     */
    const getIFrameUrl = useMemo(() => {
        return `/${constants.pages.pdfRendererUrl}/${partnerID ? `${partnerID}` : ''}`
    }, [partnerID])

    // remove class "hidden box" to see iframe on the page or go to the url
    return (
        <div className="hidden-box">
            <a href={pdfDataUrl} ref={linkRef} target="_blank" rel="noreferrer" download={fileName}>Download</a>
            <iframe src={getIFrameUrl} ref={application} title="PDF" width="1024" height="2000"></iframe>
        </div>
    )
}
